import { PageHeader, CTASection, Disclaimer } from "@/components/Shared";
import PricingInquiryForm from "@/components/PricingInquiryForm";
import { ExternalLink, ShieldCheck, Receipt, Info } from "lucide-react";
import { Fragment } from "react";

// Department of Home Affairs — current visa application charges (1 July 2026 schedule).
// Base application charge for the primary applicant (AUD). Subject to change without notice.
// Fee increases effective 1 July 2026 — source: Department of Home Affairs / visaenvoy.com.
// Always confirm at: https://immi.homeaffairs.gov.au/visas/getting-a-visa/fees-and-charges/current-visa-pricing
const GOVT_FEES = [
  {
    group: "Visitor Visas",
    rows: [
      { service: "Visitor Visa (Subclass 600) — Tourist Stream (offshore)", govt: "AUD 250", old: "AUD 200" },
      { service: "Visitor Visa (Subclass 600) — Tourist Stream (onshore)", govt: "AUD 500" },
      { service: "Visitor Visa (Subclass 600) — Sponsored Family Stream", govt: "AUD 250", old: "AUD 200" },
      { service: "Visitor Visa (Subclass 600) — Business Visitor Stream", govt: "AUD 250", old: "AUD 200" },
      { service: "Electronic Travel Authority (Subclass 601)", govt: "AUD 20 service charge" },
      { service: "eVisitor (Subclass 651)", govt: "No charge" },
    ],
  },
  {
    group: "Student & Graduate Visas",
    rows: [
      { service: "Student Visa (Subclass 500)", govt: "AUD 2,500", old: "AUD 2,000" },
      { service: "Student Guardian Visa (Subclass 590)", govt: "AUD 2,500", old: "AUD 2,000" },
      { service: "Temporary Graduate Visa (Subclass 485)", govt: "AUD 5,750", old: "AUD 2,300" },
    ],
  },
  {
    group: "Skilled Migration",
    rows: [
      { service: "Skilled Independent Visa (Subclass 189)", govt: "AUD 6,135", old: "AUD 4,910" },
      { service: "Skilled Nominated Visa (Subclass 190)", govt: "AUD 6,135", old: "AUD 4,910" },
      { service: "Skilled Work Regional Visa (Subclass 491)", govt: "AUD 6,135", old: "AUD 4,910" },
      { service: "Skilled Regional Permanent (Subclass 191)", govt: "AUD 565" },
    ],
  },
  {
    group: "Employer Sponsored",
    rows: [
      { service: "Skills in Demand (Subclass 482) — Core Skills / Specialist", govt: "AUD 4,015", old: "AUD 3,210" },
      { service: "Employer Nomination Scheme (Subclass 186)", govt: "AUD 6,140", old: "AUD 4,910" },
      { service: "Skilled Employer Sponsored Regional (Subclass 494)", govt: "AUD 6,140", old: "AUD 4,910" },
      { service: "Training Visa (Subclass 407)", govt: "AUD 405" },
      { service: "Standard Business Sponsorship — Nomination", govt: "AUD 540 + nom. fee" },
    ],
  },
  {
    group: "Family & Partner Visas",
    rows: [
      { service: "Partner Visa (Subclass 820/801 · 309/100)", govt: "AUD 11,710", old: "AUD 9,365" },
      { service: "Prospective Marriage Visa (Subclass 300)", govt: "AUD 11,710", old: "AUD 9,365" },
      { service: "Parent — Contributory (Subclass 143) — first instalment", govt: "AUD 6,300 + second VAC", old: "AUD 5,040" },
      { service: "Parent (Subclass 103 · 804) — first instalment", govt: "AUD 555 – 6,300", old: "AUD 445 – 5,040" },
      { service: "Child Visa (Subclass 101 / 802)", govt: "AUD 3,470" },
    ],
  },
  {
    group: "National Innovation & Talent",
    rows: [
      { service: "National Innovation Visa (Subclass 858)", govt: "AUD 4,840" },
    ],
  },
  {
    group: "Citizenship",
    rows: [
      { service: "Citizenship by Conferral", govt: "AUD 575" },
      { service: "Citizenship by Descent", govt: "AUD 365" },
    ],
  },
  {
    group: "Reviews & Appeals",
    rows: [
      { service: "Administrative Review Tribunal (ART) Application", govt: "Refer to ART fee schedule" },
    ],
  },
];

// StudyNet Migration — Professional Service Fees (from official fee schedule).
// All amounts in AUD, indicative ranges only, + GST where stated.
// Final professional fee is confirmed in a written engagement letter after assessment.
const STUDYNET_FEES = [
  {
    group: "Visitor Visa",
    rows: [
      { service: "Visitor Visa (Subclass 600) — Visa Application", price: "From AUD 600 – 1,100 + GST" },
    ],
  },
  {
    group: "Student & Graduate Visas",
    rows: [
      { service: "Initial 485 Visa — Visa Application", price: "From AUD 600 – 1,100 + GST" },
      { service: "485 Subsequent Entrant Visa — Visa Application", price: "From AUD 1,000 – 1,500 + GST" },
    ],
  },
  {
    group: "Training Visa (Subclass 407)",
    rows: [
      { service: "Nomination", price: "From AUD 1,750 – 2,000 + GST" },
      { service: "Visa Application", price: "From AUD 1,500 – 2,000 + GST" },
      { service: "Training Plan", price: "From AUD 1,000" },
      { service: "TAS Sponsorship", price: "From AUD 750 – 1,000 + GST" },
    ],
  },
  {
    group: "Skills in Demand (Subclass 482)",
    rows: [
      { service: "SBS Sponsorship", price: "From AUD 1,000 – 1,500 + GST" },
      { service: "Nomination", price: "From AUD 2,250 – 2,750 + GST" },
      { service: "Visa Application", price: "From AUD 2,250 – 2,750 + GST" },
      { service: "Skill Assessment", price: "From AUD 1,000 – 1,500 + GST" },
    ],
  },
  {
    group: "Employer Nomination Scheme (Subclass 186)",
    rows: [
      { service: "Nomination", price: "From AUD 2,500 – 4,000 + GST" },
      { service: "Skill Assessment", price: "From AUD 1,000 – 1,500 + GST" },
      { service: "Visa Application", price: "From AUD 2,500 – 4,000 + GST" },
    ],
  },
  {
    group: "Partner Visa / NZ Partner",
    rows: [
      { service: "820 Visa Application", price: "From AUD 3,300 – 4,400 + GST" },
      { service: "801 Visa Application", price: "From AUD 1,000 – 2,000 + GST" },
    ],
  },
  {
    group: "Prospective Marriage Visa (Subclass 300)",
    rows: [
      { service: "Visa Application", price: "From AUD 4,000 + GST" },
    ],
  },
  {
    group: "Skilled Migration (491 / 189 / 190)",
    rows: [
      { service: "Complete Service Package", price: "From AUD 3,300 – 4,000 + GST" },
      { service: "Skill Assessment", price: "From AUD 1,000 – 1,500 + GST" },
      { service: "EOI", price: "Included" },
      { service: "State Nomination", price: "Included" },
      { service: "Visa Application", price: "From AUD 2,300 – 2,500 + GST" },
    ],
  },
  {
    group: "National Innovation Visa (Subclass 858)",
    rows: [
      { service: "Expression of Interest (EOI)", price: "From AUD 4,000 + GST" },
      { service: "Visa Application", price: "From AUD 4,000 + GST" },
    ],
  },
  {
    group: "Aged Parent Visa (Subclass 804)",
    rows: [
      { service: "Visa Application", price: "From AUD 5,000 + GST" },
    ],
  },
  {
    group: "ART Appeals",
    rows: [
      { service: "ART Appeal — Student Visas", price: "Consultation Required" },
      { service: "ART Appeal — Other Visas", price: "Consultation Required" },
    ],
  },
  {
    group: "Consultations",
    rows: [
      { service: "Confidential Consultation (Phone · Zoom · In-Person · 30 minutes)", price: "AUD 110" },
      { service: "Document Review (per document)", price: "From AUD 88 – 220" },
    ],
  },
];

const HIGHLIGHTS = [
  { icon: ShieldCheck, title: "Transparent Pricing", desc: "Our professional service fee range is shared upfront. Final scope is confirmed in a written engagement letter before any work begins." },
  { icon: Receipt, title: "Government Fees Separate", desc: "All Department of Home Affairs visa application charges (VAC) are payable directly to the Department and listed separately on this page." },
  { icon: Info, title: "Indicative Ranges", desc: "Professional fees may vary depending on complexity, urgency, documents required, refusal history and individual circumstances." },
];

function FeeTable({ data, accent, header, valueLabel }) {
  return (
    <div className="overflow-x-auto border border-slate-200">
      <table data-testid={`fee-table-${accent}`} className="w-full text-sm">
        <thead>
          <tr className={`${accent === "govt" ? "bg-[#29324A]" : "bg-[#29324A]"} text-white`}>
            <th className="text-left px-4 md:px-6 py-4 font-display text-base">{header}</th>
            <th className={`text-left px-4 md:px-6 py-4 font-display text-base whitespace-nowrap ${accent === "studynet" ? "bg-[#C43A89]" : ""}`}>{valueLabel}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((g, gi) => (
            <Fragment key={`g-${gi}`}>
              <tr className="bg-[#F8F4FB]">
                <td colSpan={2} className="px-4 md:px-6 py-3 text-[10px] font-bold tracking-[0.2em] text-[#29324A] uppercase">
                  {g.group}
                </td>
              </tr>
              {g.rows.map((r, ri) => (
                <tr key={`r-${gi}-${ri}`} className="border-t border-slate-100 hover:bg-[#FCFBFD] transition-colors">
                  <td className="px-4 md:px-6 py-4 text-slate-700 leading-snug">{r.service}</td>
                  <td className={`px-4 md:px-6 py-4 whitespace-nowrap font-semibold ${accent === "studynet" ? "text-[#C43A89]" : "text-slate-800"}`}>
                    {r.old && (
                      <span className="text-slate-400 line-through mr-2 font-normal">{r.old}</span>
                    )}
                    <span>{r.govt || r.price}</span>
                    {r.old && (
                      <span className="ml-2 inline-flex items-center text-[9px] font-bold tracking-[0.18em] uppercase text-[#C43A89] bg-[#C43A89]/10 px-1.5 py-0.5 align-middle">New</span>
                    )}
                  </td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ServiceCharges() {
  return (
    <div>
      <PageHeader
        eyebrow="Transparent Pricing · No Hidden Fees"
        title="Visa Service Fees."
        subtitle="Government visa application charges and our professional service fees — clearly separated, professionally explained, with no hidden costs."
        image="/assets/sydney-harbour-sunset.jpg"
      />

      <section className="py-16 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-px bg-slate-200">
            {HIGHLIGHTS.map((h, i) => (
              <div key={i} className="bg-white p-7">
                <h.icon className="h-7 w-7 text-[#C43A89] mb-4" />
                <h3 className="font-display text-xl text-[#29324A] mb-2">{h.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-600 leading-relaxed italic">
            Professional fees may vary depending on the complexity of the matter, individual circumstances, urgency and level of professional work required. A final quote will be provided after assessment.
          </p>
        </div>
      </section>

      {/* Table 1 — Government */}
      <section data-testid="govt-fee-section" className="pb-12 md:pb-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
              <span className="brand-dot bg-[#C43A89]" />
              Table 1
            </div>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#29324A] leading-tight">
                Department of Home Affairs — Visa Application Charges
              </h2>
              <span data-testid="fee-schedule-badge" className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] uppercase text-white bg-gradient-to-r from-[#29324A] to-[#C43A89] px-3.5 py-2">
                Updated · 1 July 2026
              </span>
            </div>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-3xl">
              The base application charges below reflect the current Department of Home Affairs schedule effective <strong className="text-[#29324A]">1 July 2026</strong>. Where fees increased in July 2026, the previous fee is shown crossed out for reference. Additional applicant charges, second instalments, surcharges and non-internet charges may apply. Always confirm the current charge at the time of lodgement.
            </p>
            <a
              href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/fees-and-charges/current-visa-pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-[#29324A] hover:text-[#C43A89] transition-colors border-b border-[#29324A] pb-0.5"
            >
              View current visa pricing on immi.homeaffairs.gov.au <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>

          <FeeTable data={GOVT_FEES} accent="govt" header="Visa Category" valueLabel="Base Application Charge (AUD)" />
        </div>
      </section>

      {/* Table 2 — StudyNet */}
      <section data-testid="studynet-fee-section" className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
              <span className="brand-dot bg-[#C43A89]" />
              Table 2
            </div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#29324A] leading-tight">
              Our Professional Service Fees
            </h2>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-3xl">
              The ranges below indicate our professional service fees (excluding GST where stated as "+ GST"). They do <strong>not</strong> include the Department of Home Affairs visa application charges in Table 1, or any third-party costs. A tailored quote is provided after an initial assessment.
            </p>
          </div>

          <FeeTable data={STUDYNET_FEES} accent="studynet" header="Service" valueLabel="Our Fee Range" />
        </div>
      </section>

      <section data-testid="pricing-disclaimer" className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-6 border-l-4 border-[#C43A89] bg-[#F8F4FB]">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2 flex items-center gap-2">
              <Info className="h-3.5 w-3.5" /> Important Fee Disclaimer
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              All professional service fees listed are indicative only and may vary depending on the client's individual circumstances, visa history, complexity of the matter, urgency and documents required. Government visa application charges, medical examination fees, police checks, skills assessment fees, English test fees, translation fees, and other third-party costs are not included unless specifically stated.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <PricingInquiryForm />
        </div>
      </section>

      <section className="bg-white py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Disclaimer>
            Migration advice on this page is general information and does not replace personalised advice. Final scope, inclusions and total professional fee will be confirmed in a written engagement letter before any work begins.
          </Disclaimer>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
